import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Eye, Share2, Download, TrendingUp, Trophy, Rocket, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const CHANNEL_LABEL = {
  twitter: "X (Twitter)", facebook: "Facebook", linkedin: "LinkedIn",
  whatsapp: "WhatsApp", instagram: "Instagram", email: "Email", copy: "Direct link",
};

export default function HostGrowthTile() {
  const [d, setD] = useState(null);
  const [upgrading, setUpgrading] = useState(null);
  useEffect(() => { api.get("/hosts/growth").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === null) return <div className="p-6 text-slate-500 text-sm">Loading growth signals…</div>;
  if (d === false) return null;

  const startUpgrade = (propertyId) => {
    if (upgrading) return;
    // Open a blank tab IMMEDIATELY inside the user gesture to bypass mobile popup blockers.
    const win = window.open("", "_blank");
    setUpgrading(propertyId);
    api.post("/certifications/upgrade-checkout", {
      property_id: propertyId, origin_url: window.location.origin,
    })
      .then(({ data }) => {
        if (!data?.checkout_url) throw new Error("No checkout URL");
        if (win && !win.closed) {
          win.location.href = data.checkout_url;
          toast.success("Checkout opened in a new tab");
        } else {
          // Popup blocked — best-effort fallbacks
          try { window.top.location.href = data.checkout_url; }
          catch { window.location.href = data.checkout_url; }
        }
      })
      .catch((e) => {
        if (win && !win.closed) win.close();
        // eslint-disable-next-line no-console
        console.error("Upgrade checkout failed:", e);
        toast.error(fmtErr(e) || "Could not start upgrade. Please try again.");
      })
      .finally(() => setUpgrading(null));
  };

  const stats = [
    { icon: Eye,      label: "Badge page views", value: d.total_views,     color: "text-emerald-600 bg-emerald-50" },
    { icon: Share2,   label: "One-click shares", value: d.total_shares,    color: "text-sky-600 bg-sky-50" },
    { icon: Download, label: "Badge downloads",  value: d.total_downloads, color: "text-[#0A1128] bg-[#F9F3E5]" },
  ];
  const totalEvents = stats.reduce((s, x) => s + x.value, 0);
  const zeroState = totalEvents === 0;

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8" data-testid="growth-tile">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Growth</div>
          <h2 className="mt-1 font-serif text-2xl text-[#0A1128] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" /> Your certified reach
          </h2>
        </div>
        <div className="text-xs text-slate-400">Last 14 days</div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="p-4 rounded-2xl border border-slate-200" data-testid={`growth-stat-${s.label}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${s.color}`}>
              <s.icon className="w-4 h-4" />
            </div>
            <div className="mt-3 font-serif text-3xl text-[#0A1128]">{s.value}</div>
            <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold mb-3">Activity over time</div>
          <div className="h-40">
            <ResponsiveContainer>
              <BarChart data={d.series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} tickFormatter={(v) => v.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} allowDecimals={false} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="events" radius={[6, 6, 0, 0]}>
                  {d.series.map((_, i) => <Cell key={i} fill="#10B981" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500 font-semibold mb-3">Top channels</div>
          {d.by_channel.length === 0 ? (
            <div className="text-xs text-slate-400">No shares yet. Post from the Marketing Kit to start tracking.</div>
          ) : (
            <ul className="space-y-2">
              {d.by_channel.slice(0, 6).map((c) => (
                <li key={c.channel} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{CHANNEL_LABEL[c.channel] || c.channel}</span>
                  <span className="font-serif text-lg text-[#0A1128]">{c.count}</span>
                </li>
              ))}
            </ul>
          )}
          {d.top_property && (
            <div className="mt-5 p-3 rounded-xl border border-[#D4AF37] bg-[#F9F3E5]" data-testid="growth-top-property">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#0A1128] font-semibold">
                <Trophy className="w-3.5 h-3.5 text-[#D4AF37]" /> Top performer
              </div>
              <div className="mt-1 font-medium text-[#0A1128] text-sm">{d.top_property.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{d.top_property.events} total events</div>
            </div>
          )}
        </div>
      </div>

      {zeroState && (
        <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600" data-testid="growth-zero">
          Every time a guest opens your public badge page or you post from the Marketing Kit, it lands here — proof of ROI for your subscription.
        </div>
      )}

      {d.upgrade_eligible?.length > 0 && (
        <div className="mt-6" data-testid="upgrade-nudge">
          {d.upgrade_eligible.map((u) => (
            <div
              key={u.id}
              onClick={() => startUpgrade(u.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); startUpgrade(u.id); } }}
              role="button"
              tabIndex={0}
              aria-label={`Upgrade ${u.name} to Premium for $80`}
              data-testid={`upgrade-${u.id}`}
              className="rounded-2xl border-2 border-[#D4AF37] bg-gradient-to-br from-[#F9F3E5] to-white p-5 md:p-6 flex flex-wrap items-center justify-between gap-4 cursor-pointer hover:-translate-y-0.5 hover:shadow-xl transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
            >
              <div className="flex items-start gap-4 flex-1 min-w-[240px]">
                <div className="w-11 h-11 rounded-2xl bg-[#0A1128] flex items-center justify-center shrink-0">
                  <Rocket className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#0A1128] font-semibold">Growth Milestone</div>
                  <div className="mt-1 font-serif text-xl text-[#0A1128]">{u.name} crossed {u.events_30d} reach events this month.</div>
                  <div className="text-sm text-slate-600 mt-1">You&apos;re outperforming — upgrade to <b>Premium Certified™</b> for $80 to lock in the gold badge, 24h re-verification, and featured placement.</div>
                </div>
              </div>
              <Button
                type="button"
                onClick={(e) => { e.stopPropagation(); startUpgrade(u.id); }}
                disabled={upgrading === u.id}
                data-testid={`upgrade-cta-${u.id}`}
                className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white h-11 px-6"
              >
                {upgrading === u.id ? "Redirecting…" : (<>Upgrade — $80 <ArrowUpRight className="w-4 h-4 ml-1" /></>)}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
